import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';

import databot from '../../lib/default-project/bcf454acf82e4504149f7ffe07081dbc.svg';
import blockDataMove from './example-data-move.png';
import blockFollowNose from './example-follow-nose.png';
import styles from './landing-page.css';

const privacyPolicySections = [
    {
        title: '1. Hvad er Databyggeren?',
        paragraphs: [
            'Databyggeren er et browserbaseret undervisningsværktøj, hvor elever og undervisere kan arbejde med blokprogrammering, data, billeder, modeller og interaktive projekter.',
            'Databyggeren er udviklet med fokus på, at mest mulig behandling sker lokalt i brugerens browser.'
        ]
    },
    {
        title: '2. Hvem er dataansvarlig?',
        paragraphs: [
            'Ved brug af Databyggeren i undervisningssammenhæng er den dataansvarlige som udgangspunkt den skole eller kommune, der beslutter at tage værktøjet i brug og fastlægger rammerne for anvendelsen.',
            'Det er den lokale dataansvarlige skole eller kommune, der skal sikre, at brugen sker i overensstemmelse med databeskyttelsesreglerne, herunder at elever og undervisere får relevant information om brugen.'
        ]
    },
    {
        title: '3. Hvilke oplysninger kan behandles?',
        paragraphs: [
            'Databyggeren kan som udgangspunkt anvendes uden personoplysninger, hvis brugeren kun arbejder med fiktive, anonyme eller ikke-personhenførbare data.',
            'Der kan dog behandles personoplysninger, hvis brugeren selv indtaster eller anvender oplysninger, der kan identificere en person. Det kan fx være:'
        ],
        bullets: [
            'navne',
            'billeder af identificerbare personer',
            'lydoptagelser eller tale',
            'projektindhold, hvor personer kan genkendes',
            'oplysninger om elever, klasser eller andre personer'
        ],
        closing: 'Databyggeren bør ikke anvendes til følsomme personoplysninger, fx oplysninger om helbred, diagnoser, religion, etnicitet, sociale forhold eller lignende, medmindre skolen eller kommunen særskilt har godkendt dette.'
    },
    {
        title: '4. Hvordan behandles data?',
        paragraphs: [
            'Den almindelige brug af Databyggeren sker lokalt i brugerens browser. Projektdata, blokke, figurer, variabler og eventuelle træningseksempler behandles i browserens hukommelse.',
            'Der sker ikke central lagring af brugerens projektindhold hos Databyggeren. Der føres ikke brugerlogs, anvendes ikke analytics, og der foretages ikke tracking af brugernes aktivitet.',
            'Hvis brugeren gemmer sit projekt, sker det som en lokal fil på brugerens egen computer eller enhed.'
        ]
    },
    {
        title: '5. Brug af kamera og billeder',
        paragraphs: [
            'Databyggeren kan anvende kameraet, hvis brugeren aktiverer billed- eller datatræning. Kameradata behandles lokalt i browseren.',
            'Kameraoptagelser sendes ikke til Databyggerens udbyder, og der sker ikke central lagring af kameradata.',
            'Hvis brugeren gemmer et projekt med billedtræningsdata, kan projektfilen dog indeholde billedeksempler. Hvis disse billeder viser identificerbare personer, skal projektfilen betragtes som indeholdende personoplysninger.',
            'Det anbefales derfor, at Databyggeren som udgangspunkt bruges med genstande, tegninger, symboler eller billeder uden identificerbare personer.'
        ]
    },
    {
        title: '6. Lokal lagring og gemte filer',
        paragraphs: [
            'Databyggeren kan gemme projekter som lokale filer på brugerens computer. En gemt projektfil kan indeholde:'
        ],
        bullets: [
            'projektets blokke og kode',
            'figurer, lyde og billeder',
            'variabler og data',
            'eventuelle billedtræningseksempler',
            'projektets titel'
        ],
        closing: 'Gemte filer ligger uden for Databyggerens kontrol. Brugeren, skolen eller kommunen er ansvarlig for, hvordan filerne opbevares, deles og slettes.'
    },
    {
        title: '7. Tredjepartskomponenter',
        paragraphs: [
            'Databyggeren bygger på Scratch-teknologi og kan anvende tredjepartskomponenter til funktioner i browseren, herunder komponenter til maskinlæring.',
            'I standardopsætningen sendes brugerens projektindhold og billeder ikke til disse komponenter. Nogle tekniske biblioteker kan dog blive hentet fra eksterne kilder, fx et CDN, medmindre de hostes lokalt.',
            'Hvis tale-til-tekst-funktioner anvendes, kan mikrofondata blive sendt til en ekstern talegenkendelsestjeneste. Denne funktion bør kun anvendes, hvis skolen eller kommunen har godkendt det.'
        ]
    },
    {
        title: '8. Cookies, logs og analytics',
        paragraphs: [
            'Databyggeren anvender ikke analytics, tracking eller brugerlogs.',
            'Der kan anvendes lokal teknisk lagring i browseren til funktioner som temaindstillinger eller hardwareopsætning, fx micro:bit. Denne lagring bruges ikke til tracking.'
        ]
    },
    {
        title: '9. Sletning',
        paragraphs: [
            'Data, der kun behandles i browserens aktive hukommelse, slettes normalt, når siden lukkes, eller når brugeren rydder projektet/træningen.',
            'Lokalt gemte projektfiler slettes ikke automatisk af Databyggeren. De skal slettes manuelt af brugeren eller efter skolens/kommunens lokale retningslinjer.'
        ]
    },
    {
        title: '10. Brugernes rettigheder',
        paragraphs: [
            'Hvis der behandles personoplysninger i forbindelse med brug af Databyggeren, har den registrerede efter databeskyttelsesreglerne en række rettigheder. Det kan blandt andet være ret til:'
        ],
        bullets: [
            'indsigt',
            'berigtigelse',
            'sletning',
            'begrænsning af behandling',
            'indsigelse'
        ],
        closing: 'Henvendelser om rettigheder skal rettes til den skole eller kommune, der er dataansvarlig for den konkrete brug af Databyggeren.'
    },
    {
        title: '11. Anbefalet brug',
        paragraphs: [
            'For at minimere behandling af personoplysninger anbefales det, at Databyggeren anvendes efter disse principper:'
        ],
        bullets: [
            'brug fiktive eller anonymiserede data',
            'undgå navne og andre direkte personoplysninger',
            'undgå billeder af identificerbare personer',
            'brug genstande, tegninger eller symboler til billedtræning',
            'gem og del kun projektfiler efter skolens/kommunens retningslinjer',
            'brug ikke tale-til-tekst uden særskilt lokal godkendelse'
        ]
    },
    {
        title: '12. Kontakt',
        paragraphs: [
            'Spørgsmål om den konkrete brug af Databyggeren i undervisningen skal rettes til den skole eller kommune, der har besluttet at anvende værktøjet.'
        ]
    }
];

const LandingPage = props => {
    const [privacyOpen, setPrivacyOpen] = useState(false);

    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                <div className={styles.hero}>
                    <div className={styles.heroCopy}>
                        <h1 className={styles.title}>Databyggeren</h1>
                        <p className={styles.lead}>
                            Arbejd med maskinlæring, blokprogrammering og interaktive projekter direkte i browseren.
                        </p>

                        <div className={styles.heroActions}>
                            <button
                                className={styles.primaryButton}
                                onClick={props.onContinue}
                                type="button"
                            >
                                <FormattedMessage
                                    defaultMessage="Kom i gang"
                                    description="Knap til at gå videre fra landing page til editoren"
                                    id="gui.landingPage.start"
                                />
                            </button>
                            <button
                                className={styles.privacyButton}
                                onClick={() => setPrivacyOpen(!privacyOpen)}
                                type="button"
                            >
                                {privacyOpen ? 'Skjul privatlivspolitik' : 'Læs privatlivspolitik'}
                            </button>
                        </div>
                    </div>

                    <div className={styles.robotPanel}>
                        <div
                            aria-hidden="true"
                            className={styles.blockExamples}
                        >
                            <div className={`${styles.blockExampleCard} ${styles.blockExampleTop}`}>
                                <img
                                    alt=""
                                    className={styles.blockExampleImage}
                                    src={blockFollowNose}
                                />
                            </div>
                            <div className={`${styles.blockExampleCard} ${styles.blockExampleBottom}`}>
                                <img
                                    alt=""
                                    className={styles.blockExampleImage}
                                    src={blockDataMove}
                                />
                            </div>
                        </div>
                        <div className={styles.robotStage}>
                            <img
                                alt="Databot"
                                className={styles.robotImage}
                                src={databot}
                            />
                        </div>
                        <div className={styles.robotBubble}>
                            Træn mig med lyd eller billeder, og brug det til at styre dit projekt.
                        </div>
                    </div>
                </div>

                <div className={styles.frontNotice}>
                    <h2>Husk datareglerne</h2>
                    <p>
                        Databyggeren bruges til at arbejde med data og programmering. Du skal bruge fiktive
                        eller anonyme data og undgå oplysninger, der kan identificere dig selv eller andre.
                        Brug ikke rigtige navne, billeder af personer eller private oplysninger.
                    </p>
                    <p>
                        Når du arbejder i Databyggeren, behandles dit projekt som udgangspunkt lokalt i
                        browseren. Der føres ikke brugerlogs, og der bruges ikke analytics eller tracking.
                        Hvis du gemmer projektet som en fil, ligger filen på din computer eller det sted,
                        du selv gemmer den.
                    </p>
                    <p>Spørg din lærer, hvis du er i tvivl om, hvilke data du må bruge.</p>
                </div>

                {privacyOpen && (
                    <section className={styles.privacyPanel}>
                        <div className={styles.privacyHeader}>
                            <div>
                                <p className={styles.privacyKicker}>Privatlivspolitik</p>
                                <h2>Privatlivspolitik for Databyggeren</h2>
                                <p>Senest opdateret: 26. maj 2026</p>
                            </div>
                            <button
                                aria-label="Luk privatlivspolitik"
                                className={styles.closeButton}
                                onClick={() => setPrivacyOpen(false)}
                                type="button"
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.privacySummary}>
                            <section>
                                <h3>Privatliv og data</h3>
                                <p>
                                    Databyggeren er udviklet, så personoplysninger som udgangspunkt ikke behandles af CFU Danmark.
                                    Når du bruger Databyggeren, arbejder du lokalt i din egen browser. Dine data sendes ikke til
                                    CFU Danmark, dit arbejde gemmes ikke centralt hos CFU, og der anvendes hverken tracking,
                                    analytics eller brugerlogs. Hvis du gemmer et projekt, sker det som en fil på din egen enhed
                                    eller i skolens egne systemer.
                                </p>
                            </section>
                            <section>
                                <h3>Brug af data i undervisningen</h3>
                                <p>
                                    Databyggeren bør som udgangspunkt anvendes med fiktive data, anonyme data og billeder uden
                                    identificerbare personer. Du bør undgå at anvende navne og kontaktoplysninger, billeder eller
                                    video af personer samt følsomme oplysninger. Hvis der alligevel anvendes personoplysninger,
                                    er det den skole eller kommune, der anvender Databyggeren, som er ansvarlig for, hvordan
                                    oplysningerne behandles.
                                </p>
                            </section>
                            <section>
                                <h3>Kamera og lyd</h3>
                                <p>
                                    Hvis kamera eller lyd anvendes, behandles data lokalt i browseren og sendes ikke til CFU Danmark.
                                    Hvis et projekt gemmes med billeder eller lyd, kan den gemte fil indeholde personoplysninger.
                                </p>
                            </section>
                            <section>
                                <h3>Ansvar</h3>
                                <p>
                                    Ved brug af Databyggeren i undervisningen er det den skole eller kommune, der anvender værktøjet,
                                    som er dataansvarlig. CFU Danmark har ikke adgang til og behandler ikke brugerdata.
                                </p>
                            </section>
                            <section>
                                <h3>Rettigheder og spørgsmål</h3>
                                <p>
                                    Hvis der behandles personoplysninger, har brugeren rettigheder efter databeskyttelsesreglerne.
                                    Henvendelser om rettigheder skal rettes til den skole eller kommune, der anvender Databyggeren.
                                    Hvis brugeren er i tvivl om, hvilke data der må anvendes, bør vedkommende spørge sin lærer.
                                </p>
                            </section>
                        </div>

                        <p className={styles.privacyIntro}>
                            Denne privatlivspolitik beskriver, hvordan personoplysninger behandles ved brug af Databyggeren.
                        </p>

                        <div className={styles.policySections}>
                            {privacyPolicySections.map(section => (
                                <section
                                    className={styles.policySection}
                                    key={section.title}
                                >
                                    <h3>{section.title}</h3>
                                    {section.paragraphs.map(paragraph => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}
                                    {section.bullets && (
                                        <ul>
                                            {section.bullets.map(item => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {section.closing && <p>{section.closing}</p>}
                                </section>
                            ))}
                        </div>
                    </section>
                )}

                <div className={styles.creditLine}>
                    Databyggeren er udviklet af CFU Danmark i samarbejde med Ultrabytes.
                </div>
            </div>
        </div>
    );
};

LandingPage.propTypes = {
    onContinue: PropTypes.func.isRequired
};

export default LandingPage;
